import { useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ArticleCard from '@/components/base/ArticleCard';
import { articles } from '@/mocks/articles';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [slug]);

  const article = useMemo(
    () => articles.find((a) => a.slug === slug),
    [slug],
  );

  const relatedArticles = useMemo(() => {
    if (!article) return [];
    return articles
      .filter((a) => a.categorySlug === article.categorySlug && a.slug !== article.slug)
      .slice(0, 3);
  }, [article]);

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20 md:pt-24 flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
              <i className="ri-file-unknow-line text-3xl text-foreground-400"></i>
            </div>
            <h1 className="font-heading font-bold text-xl text-foreground-900 mb-2">
              文章未找到
            </h1>
            <p className="text-sm text-foreground-500 mb-6">您访问的文章可能已被移除或链接有误</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer"
            >
              <i className="ri-arrow-left-line"></i>
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: article.category, href: `/category/${article.categorySlug}` },
    { label: article.title },
  ];

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    datePublished: article.publishDate,
    publisher: {
      '@type': 'Organization',
      name: '精品久久久',
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://www.jingpin999.com/article/${article.slug}`,
    },
    keywords: article.tags.join(', '),
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }}
      />
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20 md:pt-24">
          <article>
            {/* Header */}
            <header className="bg-background-100 border-b border-background-200/70">
              <div className="max-w-4xl mx-auto px-4 md:px-6 py-8 md:py-12">
                <div className="mb-4">
                  <Breadcrumb items={breadcrumbItems} />
                </div>

                <div className="flex flex-wrap items-center gap-2 mb-4">
                  <Link
                    to={`/category/${article.categorySlug}`}
                    className="px-2.5 py-1 rounded-full bg-accent-100 text-accent-700 text-xs font-medium hover:bg-accent-200 transition-colors cursor-pointer"
                  >
                    {article.category}
                  </Link>
                  {article.tags.map((tag) => (
                    <span
                      key={tag}
                      className="px-2.5 py-1 rounded-full bg-secondary-100 text-secondary-700 text-xs"
                    >
                      {tag}
                    </span>
                  ))}
                </div>

                <h1 className="font-heading font-bold text-2xl md:text-3xl lg:text-4xl text-foreground-900 mb-4 leading-tight">
                  {article.title}
                </h1>

                <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500">
                  <div className="flex items-center gap-2">
                    <img
                      src={article.authorAvatar}
                      alt={article.author}
                      className="w-8 h-8 rounded-full object-cover"
                    />
                    <span className="font-medium text-foreground-700">{article.author}</span>
                  </div>
                  <span className="text-foreground-300">|</span>
                  <time dateTime={article.publishDate}>{article.publishDate}</time>
                  <span className="text-foreground-300">|</span>
                  <span>{article.readTime}</span>
                </div>
              </div>
            </header>

            {/* Featured Image */}
            <div className="max-w-4xl mx-auto px-4 md:px-6 pt-8 md:pt-10">
              <div className="aspect-[16/9] w-full overflow-hidden rounded-xl">
                <img
                  src={article.imageUrl}
                  alt={article.title}
                  title={`${article.title} - 精品久久久`}
                  className="w-full h-full object-cover"
                />
              </div>
            </div>

            {/* Content */}
            <div className="max-w-3xl mx-auto px-4 md:px-6 py-8 md:py-12">
              <div
                className="prose prose-lg max-w-none
                  prose-headings:font-heading prose-headings:text-foreground-900 prose-headings:font-bold
                  prose-h2:text-xl md:prose-h2:text-2xl prose-h2:mt-10 prose-h2:mb-4
                  prose-p:text-foreground-700 prose-p:leading-relaxed prose-p:mb-5
                  prose-strong:text-foreground-900 prose-strong:font-semibold
                  prose-ul:my-5 prose-li:text-foreground-700 prose-li:mb-2
                  prose-a:text-primary-600 prose-a:no-underline hover:prose-a:text-primary-700"
                dangerouslySetInnerHTML={{ __html: article.content }}
              />
            </div>

            {/* Share */}
            <div className="max-w-3xl mx-auto px-4 md:px-6 pb-8 border-b border-background-200/70">
              <div className="flex items-center gap-4">
                <span className="text-sm text-foreground-500">分享至：</span>
                <button className="w-9 h-9 flex items-center justify-center rounded-full bg-background-100 text-foreground-600 hover:bg-primary-100 hover:text-primary-600 transition-colors cursor-pointer">
                  <i className="ri-wechat-line"></i>
                </button>
                <button className="w-9 h-9 flex items-center justify-center rounded-full bg-background-100 text-foreground-600 hover:bg-primary-100 hover:text-primary-600 transition-colors cursor-pointer">
                  <i className="ri-weibo-line"></i>
                </button>
                <button className="w-9 h-9 flex items-center justify-center rounded-full bg-background-100 text-foreground-600 hover:bg-primary-100 hover:text-primary-600 transition-colors cursor-pointer">
                  <i className="ri-link"></i>
                </button>
              </div>
            </div>
          </article>

          {/* Related Articles */}
          {relatedArticles.length > 0 && (
            <section className="py-10 md:py-16">
              <div className="max-w-7xl mx-auto px-4 md:px-6">
                <h2 className="font-heading font-bold text-xl md:text-2xl text-foreground-900 mb-8">
                  相关推荐
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
                  {relatedArticles.map((a) => (
                    <ArticleCard key={a.slug} {...a} imageUrl={a.imageUrl} />
                  ))}
                </div>
              </div>
            </section>
          )}
        </main>
        <Footer />
      </div>
    </>
  );
}